import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";

import {
    BackgroundColor,
    SECONDARY_COLOR_LIGHT,
} from "@hermes/aphrodite/expansion-panel";
import { ProductToggles } from "@hermes/api-model-product";

@Component({
    selector: "h-product-toggles",
    templateUrl: "./product-toggles.component.html",
    styleUrls: ["./product-toggles.component.scss"],
})
/**
 * Component use to display infos about product into toggles.
 * There is one toggle by couple title/content of each info :
 * - product details
 * - more info
 * - care instructions
 * - delivery and returns
 * - selling conditions
 * - gift
 *
 * Only one toggle can be open at once.
 */
export class ProductTogglesComponent implements OnInit {
    @Input({ required: true })
    public productToggles!: ProductToggles;

    /** Emit when a toggle is expanded */
    @Output()
    public expandToggle: EventEmitter<
        { id: string; isExpanded: boolean } | undefined
    > = new EventEmitter<{ id: string; isExpanded: boolean } | undefined>(
        undefined,
    );

    @Input() public backgroundColor: BackgroundColor = SECONDARY_COLOR_LIGHT;

    /**
     * use to manage toggle state : only one toggle can be open.
     * If true, toggle is collapsed, false toggle is open
     */
    public togglesCollapsed: Array<{
        name: keyof ProductToggles;
        collapsed: boolean;
        staticTitle?: string;
    }> = [];

    public readonly fragmentlabelTitle = "Title";

    /**
     * define position of toggle in product page
     */
    private orderAndContainDynamicTitle: Array<{
        name: keyof ProductToggles;
        staticTitle?: string;
    }> = [
        { name: "ingredients" },
        { name: "applicationAdvice" },
        { name: "productDetail" },
        { name: "moreInformation" },
        {
            name: "careInstructions",
            staticTitle: "hermes.product-page.toggle.care-instructions",
        },
        {
            name: "deliveryAndReturns",
            staticTitle: "hermes.product-page.toggle.delivery-and-returns",
        },
        {
            name: "sellingConditions",
            staticTitle: "hermes.product-page.toggle.selling-conditions",
        },
        { name: "gift", staticTitle: "hermes.product-page.toggle.gifting" },
    ];

    public ngOnInit(): void {
        if (this.togglesCollapsed.length <= 0) {
            this.orderAndContainDynamicTitle.forEach((toggle) => {
                if (
                    this.productToggles[toggle.name] &&
                    (toggle.staticTitle ??
                        this.productToggles[
                            `${toggle.name}${this.fragmentlabelTitle}` as keyof ProductToggles
                        ])
                ) {
                    this.togglesCollapsed.push({
                        name: toggle.name,
                        collapsed: true,
                        staticTitle: toggle.staticTitle,
                    });
                }
            });
        }
    }

    public onCollapsedChange(event: { id: string; value: boolean }): void {
        this.togglesCollapsed.forEach((toggle) => {
            toggle.collapsed =
                event.id === toggle.name ? !toggle.collapsed : true;
        });
        this.expandToggle.emit({ id: event.id, isExpanded: !event.value });
    }

    public getToggleValue(key: string): string | undefined {
        return this.productToggles[key as keyof ProductToggles];
    }
}
